<template>
  <div class="flex flex-col gap-4">
    <Card no-margin>
      <CardBody>
        <div class="text-center font-medium">
          <div>
            <div class="text-xl font-semibold inline-block">
              <div v-if="agreementData.customer?.isCompany">
                <CopyText :explicit-text-to-copy="agreementData.customer.companyName || undefined">
                  <div>
                    {{ agreementData.customer.name }}
                  </div>
                </CopyText>
                <CopyText
                  :explicit-text-to-copy="
                    agreementData.customer.forename + ' ' + agreementData.customer.surname || undefined
                  "
                >
                  <div class="text-md text-quaternary -mt-1">
                    {{ agreementData.customer.forename + ' ' + agreementData.customer.surname }}
                  </div>
                </CopyText>
              </div>
              <CopyText
                v-else
                :explicit-text-to-copy="agreementData.customer.forename + ' ' + agreementData.customer.surname"
              >
                {{ agreementData.customer.title }}
                {{ agreementData.customer.name }}
              </CopyText>
            </div>
          </div>
          <div class="flex items-center justify-center mt-1 text-quaternary">
            <img
              :src="agreementData.dealership.manufacturerLogo"
              class=""
              width="18"
            />
            <h6
              v-tippy="agreementData.dealership.nameShortNoManufacturer"
              class="ml-2 text-left"
            >
              {{ agreementData.dealership.displayName }}
            </h6>
            <Icon
              v-if="agreementData.dealership.notes"
              v-tippy="agreementData.dealership.notes"
              type="file-lines"
              class="ml-2 text-quaternary cursor-help"
            />
          </div>
        </div>
        <div
          v-if="agreementData.timeZone != agreementData.dealership.timeZone"
          class="mt-2 gap-2 flex justify-center"
        >
          <Chip
            v-if="agreementData.timeZone"
            v-tippy="'Customer\'s Timezone: ' + agreementData.timeZone"
            size="sm"
            icon="user"
          >
            {{ agreementData.timeZone ? dayjs().tz(agreementData.timeZone).format('h:mma') : '-' }}
          </Chip>
          <Chip
            v-if="agreementData?.dealership?.timeZone"
            v-tippy="'Dealership\'s Timezone: ' + agreementData.dealership.timeZone"
            size="sm"
            icon="car-building"
          >
            {{
              agreementData.dealership.timeZone ? dayjs().tz(agreementData.dealership.timeZone).format('h:mma') : '-'
            }}
          </Chip>
        </div>
      </CardBody>
      <Separator class="mx-10" />
      <CardBody>
        <!-- Contact Permissions -->
        <div class="flex gap-2 justify-center mb-4">
          <div
            v-for="permission in contactPermissionItems"
            :key="permission.key"
            v-tippy="permission.tooltip"
            class="rounded-full p-1 flex items-center justify-center w-[30px] h-[30px] bg-tertiary"
          >
            <span class="fa-stack fa-2x text-sm">
              <i :class="`fa-duotone ${permission.icon} fa-stack-1x align-middle text-secondary`"></i>
              <i
                v-if="!permission.allowed"
                class="fa-duotone fa-slash-forward fa-stack-2x text-[16px] mt-[0.3rem] text-danger"
              ></i>
            </span>
          </div>
          <div>
            <Icon
              v-tippy="{
                content: $t('ui.crud.update', { item: $t('ui.common.preference', 2) })
              }"
              type="cog"
              class="hover:cursor-pointer rounded-full p-1 flex items-center justify-center w-[30px] h-[30px] border border-secondary"
              @click="$emit('open-contact-preferences')"
            />
          </div>
        </div>

        <!-- Contact Details -->
        <div>
          <div
            v-for="item in contactDetails"
            :key="item.label"
            class="mb-2 flex items-center justify-between gap-2"
          >
            <div class="flex min-w-0 flex-1">
              <Icon
                v-tippy="item.label"
                :type="item.icon"
                class="text-quaternary mr-2"
              />
              <CopyText class="text-md min-w-0 flex-1">
                <span
                  class="block overflow-hidden text-ellipsis whitespace-nowrap"
                  :title="item.value"
                >
                  {{ item.value }}
                </span>
              </CopyText>
            </div>
            <Icon
              v-if="!item.isValid"
              v-tippy="item.validationMessage"
              type="warning"
              class="text-success mr-2"
            />
            <div
              v-tippy="{
                content:
                  selectedCallCenterCommsConfig?.emailAddress == null
                    ? 'You can\'t contact this customer via your active call center'
                    : ''
              }"
              class="flex gap-2"
            >
              <Button
                v-if="item.canSendSms"
                icon="message"
                size="sm"
                outlined
                :disabled="selectedCallCenterCommsConfig?.smsNumber == null"
                @click="$emit('new-conversation', 'sms', item.value)"
              />
              <Button
                v-if="item.canCall"
                icon="phone"
                size="sm"
                outlined
                :disabled="selectedCallCenterCommsConfig?.phoneNumber == null"
                @click="$emit('new-conversation', 'call', item.value)"
              />
              <Button
                v-if="item.canSendEmail"
                icon="at"
                size="sm"
                outlined
                :disabled="selectedCallCenterCommsConfig?.emailAddress == null"
                @click="$emit('new-conversation', 'email', item.value)"
              />
            </div>
          </div>
        </div>

        <Separator class="mx-4 my-4" />

        <!-- Address -->
        <div>
          <div class="flex">
            <Icon
              v-tippy="t('ui.entities.address.label')"
              type="location-dot"
              class="text-quaternary mr-2"
            />
            <CopyText
              class="text-md"
              :explicit-text-to-copy="agreementData.customer.address.postcode || undefined"
            >
              <div v-if="agreementData.customer.address.line1">
                {{ agreementData.customer.address.line1 }}
              </div>
              <div v-if="agreementData.customer.address.line2">
                {{ agreementData.customer.address.line2 }}
              </div>
              <div v-if="agreementData.customer.address.town">
                {{ agreementData.customer.address.town }}
                <span v-if="agreementData.customer.address.county">, {{ agreementData.customer.address.county }}</span>
              </div>
              <div v-if="agreementData.customer.address.postcode">
                {{ agreementData.customer.address.postcode }}
              </div>
            </CopyText>
          </div>

          <div
            v-if="agreementData.customer.address.postcode"
            class="mb-2"
          >
            <DirectionsMap
              v-if="agreementData?.customer?.address?.postcode"
              v-model:coordinates="routeCoordinates"
              class="mt-4 rounded-lg"
              :from="agreementData.customer.address.postcode"
              :to="agreementData.dealership.address.postcode"
              style="height: 180px"
            />
          </div>
        </div>
      </CardBody>
    </Card>

    <Card v-if="$can('view_external_systems')">
      <CardBody>
        <CardTitle :title="$t('ui.entities.externalSystems')" />
        <div
          v-if="externalSystems && externalSystems.length > 0"
          class="flex gap-2 flex-wrap justify-start"
        >
          <Dropdown
            v-for="system in externalSystems"
            :key="system.id"
          >
            <template #triggerContent>
              <Button
                outlined
                :label="getDictionaryItem('externalSystems', system.systemName)?.label"
                severity="secondary"
              />
            </template>
            <div class="p-4 flex flex-col gap-2">
              <div>
                <div class="text-sm font-semibold">{{ $t('ui.entities.url') }}</div>
                <a
                  v-if="system.baseUrl"
                  class="link"
                  :href="system.baseUrl"
                  target="_blank"
                >
                  {{ system.baseUrl }}
                  <Icon
                    type="arrow-up-right-from-square"
                    class="link ml-2"
                  />
                </a>
                <span v-else>-</span>
              </div>
              <div>
                <div class="text-sm font-semibold">{{ $t('ui.entities.username') }}</div>
                <CopyText
                  show-icon
                  class="text-secondary"
                >
                  {{ system.username }}
                </CopyText>
              </div>
              <div>
                <div class="text-sm font-semibold">{{ $t('ui.entities.password') }}</div>
                <div class="flex items-center">
                  <div class="flex gap-[2px]">
                    <div
                      v-for="num in 8"
                      :key="num"
                    >
                      <Icon
                        type="circle"
                        class="text-[0.4rem]"
                      />
                    </div>
                  </div>
                  <CopyText
                    v-if="system.password"
                    show-icon
                    :explicit-text-to-copy="system.password"
                    class="text-tertiary"
                  ></CopyText>
                </div>
              </div>
            </div>
          </Dropdown>
        </div>
        <div v-else>
          <div class="text-center text-secondary">No external systems set up</div>
        </div>
      </CardBody>
    </Card>
  </div>
</template>

<script setup lang="ts">
// composables
import { computed, onMounted, watch } from 'vue'
// stores
import { useCallCenterStore } from '@/stores/CallCenterStore'
// components
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import Icon from '@/components/icon/Icon.vue'
import Chip from '@/components/chip/Chip.vue'
import Button from '@/components/button/Button.vue'
import CopyText from '@/components/CopyText.vue'
import Separator from '@/components/separator/Separator.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DirectionsMap from '@/components/unique/DirectionsMap.vue'
// utilities
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'
import { getDictionaryItem } from '@/dictionary'
import usePhoneValidator from '@/composables/usePhoneValidator'
import { isEmail } from '@/utilities/string/isEmail'
// types
import type { Agreement, DealershipCommsConfigItem } from '@/types/domain/agreement'
import type { ExternalSystem } from '@/types/domain/externalDealershipSystem'

const { t } = useI18n()
const callCenterStore = useCallCenterStore()

interface ContactDetail {
  type: string
  icon: string
  label: string
  value: string
  isValid: boolean | null
  validationMessage: string | null
  canCall: boolean
  canSendSms: boolean
  canSendEmail: boolean
}

interface Props {
  agreementData: Agreement
  externalSystems: ExternalSystem[] | null
  routeCoordinates: Record<string, unknown>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'open-contact-preferences': []
  'new-conversation': [type: string, value: string]
  'update:routeCoordinates': [coordinates: Record<string, unknown>]
}>()

const routeCoordinates = computed({
  get: () => props.routeCoordinates,
  set: value => {
    emit('update:routeCoordinates', value)
  }
})

// Phone validators
const phoneNumberValidations = {
  mobile: usePhoneValidator(),
  landline: usePhoneValidator()
}

/**
 * Validates phone numbers from agreement data
 */
function validatePhoneNumbers(): void {
  if (props.agreementData) {
    phoneNumberValidations.mobile.validatePhoneNumber(props.agreementData.customer.contact.mobile || '')
    phoneNumberValidations.landline.validatePhoneNumber(props.agreementData.customer.contact.telephone1 || '')
  }
}

// Validate phone numbers on mount and when agreement data changes
onMounted(() => {
  validatePhoneNumbers()
})

watch(
  () => props.agreementData?.customer?.contact,
  () => {
    validatePhoneNumbers()
  },
  { deep: true }
)

/**
 * Computed contact details list with validation and action flags
 */
const contactDetails = computed(() => {
  if (!props.agreementData) {
    return []
  }

  const items: ContactDetail[] = []

  if (props.agreementData.customer.contact.telephone1) {
    const validatedData = phoneNumberValidations.landline.validatedData.value as
      | { possible?: boolean; possibility?: string }
      | undefined
    items.push({
      type: 'landline',
      icon: 'phone',
      label: t('ui.entities.comms.landline'),
      value: props.agreementData.customer.contact.telephone1,
      isValid: validatedData?.possible ?? null,
      validationMessage: validatedData?.possibility ?? null,
      canCall: true,
      canSendSms: false,
      canSendEmail: false
    })
  }

  if (props.agreementData.customer.contact.mobile) {
    const validatedData = phoneNumberValidations.mobile.validatedData.value as
      | { possible?: boolean; possibility?: string }
      | undefined
    items.push({
      type: 'mobile',
      icon: 'mobile',
      label: t('ui.entities.comms.mobile'),
      value: props.agreementData.customer.contact.mobile,
      isValid: validatedData?.possible ?? null,
      validationMessage: validatedData?.possibility ?? null,
      canCall: true,
      canSendSms: true,
      canSendEmail: false
    })
  }

  if (props.agreementData.customer.contact.email) {
    const isValid = isEmail(props.agreementData.customer.contact.email)
    items.push({
      type: 'email',
      icon: 'at',
      label: t('ui.entities.comms.email'),
      value: props.agreementData.customer.contact.email,
      isValid: isValid,
      validationMessage: isValid ? null : 'Invalid email address',
      canCall: false,
      canSendSms: false,
      canSendEmail: true
    })
  }

  return items
})

/**
 * Computed selected call center comms config based on selected call center
 */
const selectedCallCenterCommsConfig = computed<DealershipCommsConfigItem | null>(() => {
  if (!props.agreementData || !callCenterStore.selectedCallCenter) {
    return null
  }
  return (
    props.agreementData.dealership.commsConfig.find(
      config => config.callCenterId.toString() === callCenterStore.selectedCallCenter
    ) || null
  )
})

/**
 * Array of contact permission items for rendering permission icons
 */
const contactPermissionItems = computed(() => {
  const { contactPermissions } = props.agreementData
  return [
    {
      key: 'call',
      icon: 'fa-phone',
      allowed: contactPermissions.canCall,
      tooltip: contactPermissions.canCall ? 'Can Call' : "Can't Call"
    },
    {
      key: 'email',
      icon: 'fa-envelope',
      allowed: contactPermissions.canEmail,
      tooltip: contactPermissions.canEmail ? 'Can Email' : "Can't Email"
    },
    {
      key: 'sms',
      icon: 'fa-sms',
      allowed: contactPermissions.canSms,
      tooltip: contactPermissions.canSms ? 'Can Send SMS' : "Can't Send SMS"
    }
  ]
})
</script>
